// TsplLabelPreview — anteprima FEDELE dell'etichetta.
//
// Disegna ESATTAMENTE lo stesso canvas raster che viene inviato alla
// stampante (renderLabelCanvas in labelRaster.ts) e lo scala a schermo.
// È quindi WYSIWYG reale, identico al pixel: font, dimensioni, accenti,
// word-wrap e QR sono quelli che escono dalla Clabel via comando BITMAP.

import { useEffect, useRef, useState } from 'react';
import type { LabelTemplate } from '@trackeat/shared';
import { renderLabelCanvas, type RasterOptions } from '../lib/adapters/printers/labelRaster.js';

interface Props {
  template: {
    id: string;
    name: string;
    preset: string;
    width_mm: number;
    height_mm: number;
    elements: unknown[];
    required_allergens?: boolean;
  };
  data: Record<string, unknown>;
  /** px per dot a schermo (zoom). default: si adatta alla larghezza. */
  scale?: number;
  rasterOptions?: RasterOptions;
  onIssues?: (issues: string[]) => void;
}

export function TsplLabelPreview({ template, data, scale, rasterOptions, onIssues }: Props): JSX.Element {
  const [issues, setIssues] = useState<string[]>([]);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const found: string[] = [];
    let src: HTMLCanvasElement;
    try {
      src = renderLabelCanvas(
        {
          id: template.id, tenant_id: '', name: template.name,
          preset: template.preset, width_mm: template.width_mm,
          height_mm: template.height_mm, elements: template.elements as never,
          required_allergens: template.required_allergens ?? false,
        } as unknown as LabelTemplate,
        data,
        { ...rasterOptions, onIssue: message => found.push(message) },
      );
      setIssues([...new Set(found)]);
      onIssues?.([...new Set(found)]);
    } catch (error) {
      canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height); // Never leave an obsolete successful preview visible.
      const messages = [error instanceof Error ? error.message : 'Anteprima non disponibile'];
      setIssues(messages);
      onIssues?.(messages);
      return;
    }

    const wDots = src.width;
    const hDots = src.height;
    const screenW = typeof window !== 'undefined' ? window.innerWidth : 380;
    const px = scale ?? Math.max(1.2, Math.min(3, (screenW - 80) / Math.max(1, wDots)));

    canvas.width = Math.round(wDots * px);
    canvas.height = Math.round(hDots * px);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.imageSmoothingEnabled = false;
    // Carta termica + bordo etichetta.
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(src, 0, 0, wDots, hDots, 0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, canvas.width - 1, canvas.height - 1);
  }, [template, data, scale, rasterOptions, onIssues]);

  return (
    <div>
    <canvas
      ref={canvasRef}
      style={{ display: 'block', width: '100%', height: 'auto', imageRendering: 'pixelated' }}
    />
    {!onIssues && issues.length > 0 && <div role="alert" style={{ padding: 10, color: '#7f1d1d', background: '#fee2e2', fontSize: 13 }}>
      {issues.map(message => <p key={message}>{message}</p>)}
    </div>}
    </div>
  );
}
